import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Button, Spinner, Alert, Tab, Tabs, Modal, Form } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import AppIcon from './AppIcon';
import { LoadingState } from './WorkspaceUI';
import AddStockToPortfolioModal from './AddStockToPortfolioModal';
import AddMutualFundToPortfolioModal from './AddMutualFundToPortfolioModal';
import BrokerAccountsPanel from './BrokerAccountsPanel';
import CorporateActionHistory from './CorporateActionHistory';
import { BrokerAccount, StockAccountHolding } from '../types/portfolio';

const IMPORT_ALLOWED_EXT: Record<string, string[]> = {
  zerodha_tradebook:   ['.csv', '.xlsx'],
  zerodha_holdings:    ['.csv', '.xlsx'],
  groww_tradebook:     ['.csv', '.xlsx'],
  upstox_tradebook:    ['.csv', '.xlsx'],
  angel_tradebook:     ['.csv', '.xlsx'],
  icici_direct_tradebook: ['.csv', '.xlsx'],
  other_broker_tradebook: ['.csv', '.xlsx'],
  mutual_transactions: ['.csv'],
  mutual_csv:          ['.csv'],
  cas:                 ['.pdf'],
  cas_cams:            ['.pdf'],
  cas_combined:        ['.pdf'],
  cdsl_cas:            ['.pdf'],
  nsdl_cas:            ['.pdf'],
};

const IMPORT_HINT: Record<string, string> = {
  zerodha_tradebook:   'Zerodha Console → Reports → Tradebook → Download XLSX/CSV',
  zerodha_holdings:    'Zerodha Console → Portfolio → Holdings → Download XLSX/CSV. Creates auditable reconciliation adjustments, not fake trades.',
  groww_tradebook:     'Groww stock statement / tradebook export in CSV or XLSX format',
  upstox_tradebook:    'Upstox tradebook or order history export in CSV or XLSX format',
  angel_tradebook:     'Angel One tradebook / contract export in CSV or XLSX format',
  icici_direct_tradebook: 'ICICI Direct tradebook export in CSV or XLSX format',
  other_broker_tradebook: 'Any broker tradebook export with symbol/ISIN, date, side, quantity, and price columns',
  mutual_transactions: 'MF transaction CSV from your fund house or registrar',
  cas:                 'Consolidated Account Statement PDF from NSDL/CDSL',
  cas_cams:            'CAMS CAS PDF — download from camsonline.com (CAMS only)',
  cas_combined:        'Combined CAS PDF — includes both CAMS and KFintech funds',
  cdsl_cas:            'CDSL eCAS PDF — can include demat equities and demat mutual funds',
  nsdl_cas:            'NSDL eCAS PDF — can include demat equities and demat mutual funds',
};

const ACCOUNT_SCOPED_IMPORT_TYPES = new Set([
  'zerodha_tradebook', 'zerodha_holdings', 'groww_tradebook',
  'upstox_tradebook', 'angel_tradebook', 'icici_direct_tradebook',
  'other_broker_tradebook', 'cas', 'cdsl_cas', 'nsdl_cas',
]);

const IMPORT_PROVIDER: Record<string, string> = {
  zerodha_tradebook: 'zerodha',
  zerodha_holdings: 'zerodha',
  groww_tradebook: 'groww',
  upstox_tradebook: 'upstox',
  angel_tradebook: 'angel_one',
  icici_direct_tradebook: 'icici_direct',
  cdsl_cas: 'cdsl',
  nsdl_cas: 'nsdl',
};

interface MutualFundHolding {
  id: number;
  folio?: string;
  no_shares: number;
  start_date: string;
  notes: string;
  total_cost: number;
  current_nav: number;
  previous_nav: number | null;
  current_value: number;
  total_gain: number;
  day_gain: number | null;
  day_gain_percent: number | null;
  nav_date?: string | null;
  previous_nav_date?: string | null;
  xirr: number | null;
  isin: {
    id: number;
    title: string;
    stockid: number;
  };
}

interface StockHolding {
  id: number;
  no_shares: number;
  notes: string;
  current_price: number | null;
  previous_close: number | null;
  current_value: number | null;
  total_gain: number | null;
  day_gain: number | null;
  day_gain_percent: number | null;
  xirr: number | null;
  has_price?: boolean;
  price_warning?: string;
  has_adjustments?: boolean;
  adjustment_count?: number;
  account_count?: number;
  broker_account?: BrokerAccount | null;
  accounts?: StockAccountHolding[];
  isin: {
    id: number;
    symbol: string;
    name: string;
  };
}

interface Transaction {
  id: number;
  entry_kind?: 'trade' | 'adjustment';
  date: string;
  transaction_type: string;
  quantity: number;
  price: number;
  amount: number;
  source?: string;
  reference?: string;
  notes?: string;
}

interface MutualFundPortfolioTransaction extends Transaction {
  holding_id: number;
  fund_name: string;
  isin?: string;
  folio?: string;
}

interface StockPortfolioTransaction extends Transaction {
  holding_id: number;
  entry_kind: 'trade' | 'adjustment';
  stock_name: string;
  symbol: string;
  isin: string;
}

const Portfolio: React.FC = () => {
  const [mutualFunds, setMutualFunds] = useState<MutualFundHolding[]>([]);
  const [stocks, setStocks] = useState<StockHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedPortfolio, setHasLoadedPortfolio] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = ['mutual-funds', 'stocks', 'corporate-actions', 'import', 'settings'].includes(searchParams.get('tab') || '') ? searchParams.get('tab')! : 'mutual-funds';
  const [holdingSearch, setHoldingSearch] = useState('');
  const [holdingSort, setHoldingSort] = useState('name');
  const [holdingAccount, setHoldingAccount] = useState('all');
  const [detailHolding, setDetailHolding] = useState<{ item: MutualFundHolding | StockHolding; type: 'mf' | 'stock' } | null>(null);
  const [editError, setEditError] = useState('');
  const [importPreview, setImportPreview] = useState<{ columns: string[]; rows: string[][]; message: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [error, setError] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editType, setEditType] = useState<'mf' | 'stock'>('mf');

  // Transactions modal state
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showAllMFTransactionsModal, setShowAllMFTransactionsModal] = useState(false);
  const [allMFTransactions, setAllMFTransactions] = useState<MutualFundPortfolioTransaction[]>([]);
  const [loadingAllMFTransactions, setLoadingAllMFTransactions] = useState(false);
  const [allMFTransactionsError, setAllMFTransactionsError] = useState('');
  const [mfTransactionSearch, setMFTransactionSearch] = useState('');
  const [mfTransactionType, setMFTransactionType] = useState('');
  const [mfTransactionDateFrom, setMFTransactionDateFrom] = useState('');
  const [mfTransactionDateTo, setMFTransactionDateTo] = useState('');
  const [mfTransactionPage, setMFTransactionPage] = useState(1);
  const [showAllStockTransactionsModal, setShowAllStockTransactionsModal] = useState(false);
  const [allStockTransactions, setAllStockTransactions] = useState<StockPortfolioTransaction[]>([]);
  const [loadingAllStockTransactions, setLoadingAllStockTransactions] = useState(false);
  const [allStockTransactionsError, setAllStockTransactionsError] = useState('');
  const [stockTransactionSearch, setStockTransactionSearch] = useState('');
  const [stockTransactionType, setStockTransactionType] = useState('');
  const [stockTransactionDateFrom, setStockTransactionDateFrom] = useState('');
  const [stockTransactionDateTo, setStockTransactionDateTo] = useState('');
  const [stockTransactionPage, setStockTransactionPage] = useState(1);
  // Import/upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<string>('mutual_transactions');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [pdfPassword, setPdfPassword] = useState('');
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccount[]>([]);
  const [selectedBrokerAccountId, setSelectedBrokerAccountId] = useState('');

  // Add Transaction modal state
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [addTransactionType, setAddTransactionType] = useState<'mf' | 'stock'>('stock');
  const [selectedHoldingId, setSelectedHoldingId] = useState<number | null>(null);
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    txn_type: 'buy',
    quantity: '',
    price: '',
    notes: ''
  });
  const [addTransactionLoading, setAddTransactionLoading] = useState(false);
  const [addTransactionError, setAddTransactionError] = useState('');

  // Add Stock modal state
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showAddMutualFundModal, setShowAddMutualFundModal] = useState(false);

  // Delete Transaction state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<'bulk' | 'specific'>('bulk');
  const [deleteCategory, setDeleteCategory] = useState<'mf' | 'stock'>('mf');
  const [deleteHoldingId, setDeleteHoldingId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');


  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    setRefreshing(true);
    setError('');
    try {
      const [mfResponse, stocksResponse, accountsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/portfolio/mutual-funds/`),
        axios.get(`${API_BASE_URL}/portfolio/stocks/`),
        axios.get(`${API_BASE_URL}/broker-accounts/`).catch(() => ({ data: [] }))
      ]);

      setMutualFunds(mfResponse.data);
      setStocks(stocksResponse.data);
      setHasLoadedPortfolio(true);
      setBrokerAccounts(Array.isArray(accountsResponse.data) ? accountsResponse.data : []);
    } catch (err: any) {
      setError('Could not refresh holdings. Any previously loaded values are retained.');
      console.error('Portfolio data fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refreshBrokerAccounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/broker-accounts/`);
      const accounts: BrokerAccount[] = Array.isArray(response.data) ? response.data : [];
      setBrokerAccounts(accounts);
      const matches = accounts.filter(account => !['manual', 'legacy'].includes(account.provider) &&
        (!IMPORT_PROVIDER[uploadType] || account.provider === IMPORT_PROVIDER[uploadType]));
      setSelectedBrokerAccountId(current => matches.some(account => String(account.id) === current)
        ? current : matches.length === 1 ? String(matches[0].id) : '');
    } catch (err) {
      console.error('Broker account fetch error:', err);
    }
  };

  const compatibleAccounts = (importType: string) => {
    const requiredProvider = IMPORT_PROVIDER[importType];
    return brokerAccounts.filter((account) => {
      if (['manual', 'legacy'].includes(account.provider)) return false;
      return !requiredProvider || account.provider === requiredProvider;
    });
  };

  const handleUploadTypeChange = (importType: string) => {
    const matches = compatibleAccounts(importType);
    setImportPreview(null);
    setUploadType(importType);
    setSelectedBrokerAccountId(
      ACCOUNT_SCOPED_IMPORT_TYPES.has(importType) && matches.length === 1
        ? String(matches[0].id)
        : ''
    );
    setSelectedFile(null);
    setPdfPassword('');
    setUploadError('');
    setUploadSuccess('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  const hasNumber = (value: number | null | undefined): value is number => {
    return value !== null && value !== undefined && Number.isFinite(value);
  };

  const renderDailyGain = (amount: number | null | undefined, percent?: number | null) => {
    if (!hasNumber(amount)) {
      return <span className="text-muted fst-italic">—</span>;
    }

    return (
      <span className={amount >= 0 ? 'text-success' : 'text-danger'}>
        {formatCurrency(amount)}
        {hasNumber(percent) && (
          <>
            <br />
            <small>{formatPercent(percent)}</small>
          </>
        )}
      </span>
    );
  };

  const dailyGainClass = (amount: number | null) => {
    if (!hasNumber(amount)) return 'text-muted';
    return amount >= 0 ? 'text-success' : 'text-danger';
  };

  // Handle edit button click
  const handleEdit = (item: any, type: 'mf' | 'stock') => {
    setEditError('');
    setEditingItem({ ...item });
    setEditType(type);
    setShowEditModal(true);
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = editType === 'mf'
        ? `${API_BASE_URL}/holdings/mutual-funds/${editingItem.id}/`
        : `${API_BASE_URL}/holdings/stocks/${editingItem.id}/`;

      await axios.patch(endpoint, {
        notes: editingItem.notes
      }, {
        headers: { Authorization: `Token ${token}` }
      });

      // Refresh data
      await fetchPortfolioData();
      setShowEditModal(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving edit:', err);
      setEditError('Could not save your notes. Try again.');
    }
  };

  // Handle view transactions
  const handleViewTransactions = async (item: any, type: 'mf' | 'stock') => {
    setSelectedItem(item);
    setEditType(type);
    setShowTransactionsModal(true);
    setLoadingTransactions(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'mf'
        ? `${API_BASE_URL}/holdings/mutual-funds/${item.id}/transactions/`
        : `${API_BASE_URL}/holdings/stocks/${item.id}/transactions/`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Token ${token}` }
      });

      setTransactions(response.data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleViewAllMFTransactions = async () => {
    setShowAllMFTransactionsModal(true);
    setLoadingAllMFTransactions(true);
    setAllMFTransactionsError('');
    setMFTransactionPage(1);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/portfolio/mutual-fund-transactions/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setAllMFTransactions(response.data);
    } catch (err) {
      console.error('Error fetching mutual fund transaction ledger:', err);
      setAllMFTransactions([]);
      setAllMFTransactionsError('Unable to load the mutual fund transaction ledger.');
    } finally {
      setLoadingAllMFTransactions(false);
    }
  };

  const handleViewAllStockTransactions = async () => {
    setShowAllStockTransactionsModal(true);
    setLoadingAllStockTransactions(true);
    setAllStockTransactionsError('');
    setStockTransactionPage(1);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/portfolio/stock-transactions/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setAllStockTransactions(response.data);
    } catch (err) {
      console.error('Error fetching stock transaction ledger:', err);
      setAllStockTransactions([]);
      setAllStockTransactionsError('Unable to load the stock transaction ledger.');
    } finally {
      setLoadingAllStockTransactions(false);
    }
  };

  // Upload handlers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportPreview(null);
    setUploadError('');
    setUploadSuccess('');
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      const allowed = IMPORT_ALLOWED_EXT[uploadType];
      if (allowed && !allowed.includes(ext)) {
        setUploadError(
          `Wrong file type "${ext}" for this import. Expected: ${allowed.join(' or ')}.`
        );
        setSelectedFile(null);
        e.target.value = '';
      } else {
        setSelectedFile(file);
      }
    } else {
      setSelectedFile(null);
    }
  };

  const handlePreviewImport = async () => {
    if (!selectedFile) { setUploadError('Choose a statement to review.'); return; }
    if (selectedFile.size > 20 * 1024 * 1024) { setUploadError('Choose a statement smaller than 20 MB.'); return; }
    setPreviewLoading(true);
    setUploadError('');
    const data = new FormData();
    data.append('file', selectedFile);
    data.append('import_type', uploadType);
    if (selectedBrokerAccountId) data.append('broker_account_id', selectedBrokerAccountId);
    if (pdfPassword) data.append('pdf_password', pdfPassword);
    try {
      const response = await axios.post(API_BASE_URL + '/portfolio/import/preview/', data, { headers: { Authorization: 'Token ' + localStorage.getItem('token') } });
      setImportPreview(response.data);
      setImportStep(3);
    } catch (error: any) {
      setUploadError(error?.response?.data?.error || 'Could not preview this file. Check the statement and try again.');
    } finally { setPreviewLoading(false); }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }
    if (ACCOUNT_SCOPED_IMPORT_TYPES.has(uploadType) && !selectedBrokerAccountId) {
      setUploadError('Select the broker or demat account that owns this statement.');
      return;
    }
    // Re-validate extension in case import type was changed after file selection
    const ext = '.' + (selectedFile.name.split('.').pop() || '').toLowerCase();
    const allowed = IMPORT_ALLOWED_EXT[uploadType];
    if (allowed && !allowed.includes(ext)) {
      setUploadError(
        `Wrong file type "${ext}" for this import. Expected: ${allowed.join(' or ')}.`
      );
      return;
    }
    setUploadLoading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('import_type', uploadType);
      if (selectedBrokerAccountId) formData.append('broker_account_id', selectedBrokerAccountId);
      if (['cas', 'cas_cams', 'cas_combined', 'cdsl_cas', 'nsdl_cas'].includes(uploadType)) formData.append('pdf_password', pdfPassword);

      const resp = await axios.post(`${API_BASE_URL}/portfolio/import/`, formData, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setUploadSuccess(resp.data.message || 'Import complete. Your holdings have been refreshed.');
      setImportPreview(null);
      setImportStep(1);
      setSelectedFile(null);
      setPdfPassword('');
      await fetchPortfolioData(); // refresh holdings after import
    } catch (err: any) {
      console.error('Upload error:', err);
      setUploadError(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  // Add Transaction handlers
  const handleOpenAddTransaction = (type: 'mf' | 'stock', holdingId?: number) => {
    setAddTransactionType(type);
    setSelectedHoldingId(holdingId || null);
    setNewTransaction({
      date: new Date().toISOString().split('T')[0],
      txn_type: type === 'mf' ? 'purchase' : 'buy',
      quantity: '',
      price: '',
      notes: ''
    });
    setAddTransactionError('');
    setShowAddTransactionModal(true);
  };

  const handleAddTransaction = async () => {
    if (!selectedHoldingId) {
      setAddTransactionError('Please select a holding');
      return;
    }
    if (!newTransaction.quantity || !newTransaction.price) {
      setAddTransactionError('Please fill in quantity and price');
      return;
    }

    setAddTransactionLoading(true);
    setAddTransactionError('');

    try {
      const token = localStorage.getItem('token');
      const endpoint = addTransactionType === 'mf'
        ? `${API_BASE_URL}/holdings/mutual-funds/${selectedHoldingId}/transactions/`
        : `${API_BASE_URL}/holdings/stocks/${selectedHoldingId}/transactions/`;

      await axios.post(endpoint, {
        date: newTransaction.date,
        txn_type: newTransaction.txn_type,
        quantity: parseFloat(newTransaction.quantity),
        price: parseFloat(newTransaction.price),
        notes: newTransaction.notes
      }, {
        headers: { Authorization: `Token ${token}` }
      });

      setShowAddTransactionModal(false);
      // Refresh portfolio data
      fetchPortfolioData();
    } catch (err: any) {
      console.error('Add transaction error:', err);
      setAddTransactionError(err?.response?.data?.error || 'Failed to add transaction');
    } finally {
      setAddTransactionLoading(false);
    }
  };

  // Add a new stock holding through the shared transaction-style flow.
  const handleOpenAddStock = () => setShowAddStockModal(true);
  const handleOpenAddMutualFund = () => setShowAddMutualFundModal(true);


  // Delete Handlers
  const handleOpenDeleteBulk = (category: 'mf' | 'stock') => {
    setDeleteType('bulk');
    setDeleteCategory(category);
    setDeleteHoldingId(null);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleOpenDeleteSpecific = (category: 'mf' | 'stock', holdingId: number) => {
    setDeleteType('specific');
    setDeleteCategory(category);
    setDeleteHoldingId(holdingId);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const token = localStorage.getItem('token');

      if (deleteType === 'bulk') {
        await axios.post(`${API_BASE_URL}/portfolio/delete-transactions/all/`, {
          type: deleteCategory
        }, {
          headers: { Authorization: `Token ${token}` }
        });
      } else {
        // Specific
        if (!deleteHoldingId) return;
        await axios.delete(`${API_BASE_URL}/portfolio/delete-transactions/holding/${deleteCategory}/${deleteHoldingId}/`, {
          headers: { Authorization: `Token ${token}` }
        });
      }

      setDeleteSuccess(`Successfully cleared ${deleteType === 'bulk' ? 'all' : 'selected'} ${deleteCategory === 'mf' ? 'mutual fund' : 'stock'} transactions.`);
      setShowDeleteModal(false);
      fetchPortfolioData(); // Refresh totals

      // Auto clear success message
      setTimeout(() => setDeleteSuccess(''), 5000);

    } catch (err: any) {
      console.error('Delete error:', err);
      setDeleteError(err?.response?.data?.error || 'Failed to delete transactions');
    } finally {
      setDeleteLoading(false);
    }
  };


  if (loading) {
    return <LoadingState label="Loading your holdings" />;
  }


  if (!hasLoadedPortfolio) return <div className="portfolio-page"><h1>Portfolio</h1><Alert variant="warning">Holdings are unavailable. Retry to load your investments.</Alert><Button onClick={fetchPortfolioData} disabled={refreshing}>{refreshing ? 'Retrying…' : 'Retry'}</Button></div>;

  const totalMFValue = mutualFunds.reduce((sum, mf) => sum + mf.current_value, 0);
  const totalMFCost = mutualFunds.reduce((sum, mf) => sum + mf.total_cost, 0);
  const totalMFGain = totalMFValue - totalMFCost;
  const fundsWithDailyGain = mutualFunds.filter(fund => hasNumber(fund.day_gain));
  const totalMFDailyGain = fundsWithDailyGain.length > 0
    ? fundsWithDailyGain.reduce((sum, fund) => sum + (fund.day_gain as number), 0)
    : null;
  const totalMFPreviousValue = hasNumber(totalMFDailyGain) ? totalMFValue - totalMFDailyGain : 0;
  const totalMFDailyGainPercent = hasNumber(totalMFDailyGain) && totalMFPreviousValue > 0
    ? (totalMFDailyGain / totalMFPreviousValue) * 100
    : null;

  // Stocks with valid live prices
  const pricedStocks = stocks.filter(
    stock => stock.current_value !== null && stock.current_value !== undefined
  );
  // Stocks where we have no current price (excluded from totals)
  const unpricedStocks = stocks.filter(
    stock => stock.current_value === null || stock.current_value === undefined
  );

  const totalStockValue = pricedStocks.reduce((sum, stock) => sum + (stock.current_value as number), 0);

  const totalStockGain = stocks
    .filter(stock => stock.total_gain !== null && stock.total_gain !== undefined)
    .reduce((sum, stock) => sum + (stock.total_gain as number), 0);
  const stocksWithDailyGain = stocks.filter(stock => hasNumber(stock.day_gain));
  const totalStockDailyGain = stocksWithDailyGain.length > 0
    ? stocksWithDailyGain.reduce((sum, stock) => sum + (stock.day_gain as number), 0)
    : null;
  const totalStockPreviousValue = hasNumber(totalStockDailyGain) ? totalStockValue - totalStockDailyGain : 0;
  const totalStockDailyGainPercent = hasNumber(totalStockDailyGain) && totalStockPreviousValue > 0
    ? (totalStockDailyGain / totalStockPreviousValue) * 100
    : null;

  const totalPortfolioValue = totalMFValue + totalStockValue;
  const totalPortfolioCost = totalMFCost + (totalStockValue - totalStockGain);
  const totalPortfolioGain = totalMFGain + totalStockGain;
  const totalPortfolioDailyGain = hasNumber(totalMFDailyGain) || hasNumber(totalStockDailyGain)
    ? (totalMFDailyGain || 0) + (totalStockDailyGain || 0)
    : null;
  const totalPortfolioPreviousValue = hasNumber(totalPortfolioDailyGain)
    ? totalPortfolioValue - totalPortfolioDailyGain
    : 0;
  const totalPortfolioDailyGainPercent = hasNumber(totalPortfolioDailyGain) && totalPortfolioPreviousValue > 0
    ? (totalPortfolioDailyGain / totalPortfolioPreviousValue) * 100
    : null;
  const transactionSearch = mfTransactionSearch.trim().toLowerCase();
  const filteredMFTransactions = allMFTransactions.filter((txn) => {
    const matchesSearch = !transactionSearch || [
      txn.fund_name,
      txn.folio,
      txn.isin,
      txn.transaction_type,
      txn.notes,
    ].some((value) => (value || '').toLowerCase().includes(transactionSearch));
    const matchesType = !mfTransactionType || txn.transaction_type === mfTransactionType;
    const matchesDateFrom = !mfTransactionDateFrom || txn.date >= mfTransactionDateFrom;
    const matchesDateTo = !mfTransactionDateTo || txn.date <= mfTransactionDateTo;
    return matchesSearch && matchesType && matchesDateFrom && matchesDateTo;
  });
  const mfTransactionTypes = Array.from(
    new Set(allMFTransactions.map((txn) => txn.transaction_type).filter(Boolean))
  ).sort();
  const mfTransactionPageSize = 100;
  const mfTransactionPageCount = Math.max(
    1,
    Math.ceil(filteredMFTransactions.length / mfTransactionPageSize)
  );
  const safeMFTransactionPage = Math.min(mfTransactionPage, mfTransactionPageCount);
  const pagedMFTransactions = filteredMFTransactions.slice(
    (safeMFTransactionPage - 1) * mfTransactionPageSize,
    safeMFTransactionPage * mfTransactionPageSize
  );
  const filteredPurchaseTotal = filteredMFTransactions
    .filter((txn) => !/redempt|sell|switch.?out/i.test(txn.transaction_type))
    .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
  const filteredRedemptionTotal = filteredMFTransactions
    .filter((txn) => /redempt|sell|switch.?out/i.test(txn.transaction_type))
    .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
  const compatibleImportAccounts = compatibleAccounts(uploadType);
  const stockAccountOptions = stocks.flatMap((stock) => {
    const accountRows = stock.accounts || [];
    if (accountRows.length === 0) {
      return [{ holdingId: stock.id, label: `${stock.isin.symbol} - Manual / Unassigned` }];
    }
    return accountRows.map((row) => ({
      holdingId: row.holding_id,
      label: `${stock.isin.symbol} - ${row.broker_account.display_name}`,
    }));
  });

  const stockTransactionSearchTerm = stockTransactionSearch.trim().toLowerCase();
  const filteredStockTransactions = allStockTransactions.filter((txn) => {
    const matchesSearch = !stockTransactionSearchTerm || [
      txn.stock_name,
      txn.symbol,
      txn.isin,
      txn.transaction_type,
      txn.source,
      txn.reference,
      txn.notes,
    ].some((value) => (value || '').toLowerCase().includes(stockTransactionSearchTerm));
    const matchesType = !stockTransactionType || txn.transaction_type === stockTransactionType;
    const matchesDateFrom = !stockTransactionDateFrom || txn.date >= stockTransactionDateFrom;
    const matchesDateTo = !stockTransactionDateTo || txn.date <= stockTransactionDateTo;
    return matchesSearch && matchesType && matchesDateFrom && matchesDateTo;
  });
  const stockTransactionTypes = Array.from(
    new Set(allStockTransactions.map((txn) => txn.transaction_type).filter(Boolean))
  ).sort();
  const stockTransactionPageSize = 100;
  const stockTransactionPageCount = Math.max(
    1,
    Math.ceil(filteredStockTransactions.length / stockTransactionPageSize)
  );
  const safeStockTransactionPage = Math.min(stockTransactionPage, stockTransactionPageCount);
  const pagedStockTransactions = filteredStockTransactions.slice(
    (safeStockTransactionPage - 1) * stockTransactionPageSize,
    safeStockTransactionPage * stockTransactionPageSize
  );
  const filteredStockBuyTotal = filteredStockTransactions
    .filter((txn) => txn.entry_kind === 'trade' && /buy|purchase/i.test(txn.transaction_type))
    .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
  const filteredStockSellTotal = filteredStockTransactions
    .filter((txn) => txn.entry_kind === 'trade' && /sell/i.test(txn.transaction_type))
    .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
  const filteredStockAdjustmentCount = filteredStockTransactions
    .filter((txn) => txn.entry_kind === 'adjustment').length;

  const query = holdingSearch.trim().toLowerCase();
  const compare = (left: MutualFundHolding | StockHolding, right: MutualFundHolding | StockHolding) => {
    if (holdingSort === 'name') return ('title' in left.isin ? left.isin.title : left.isin.symbol).localeCompare('title' in right.isin ? right.isin.title : right.isin.symbol);
    const key = holdingSort === 'value' ? 'current_value' : 'total_gain';
    if (!hasNumber(left[key])) return hasNumber(right[key]) ? 1 : 0;
    if (!hasNumber(right[key])) return -1;
    return holdingSort === 'loss' ? left[key]! - right[key]! : right[key]! - left[key]!;
  };
  const filteredFunds = mutualFunds.filter(item => [item.isin.title, String(item.isin.stockid), item.folio || ''].some(value => value.toLowerCase().includes(query))).sort(compare);
  const filteredStocks = stocks.filter(item => [item.isin.symbol, item.isin.name].some(value => value.toLowerCase().includes(query)) && (holdingAccount === 'all' || item.accounts?.some(account => String(account.broker_account.id) === holdingAccount) || String(item.broker_account?.id) === holdingAccount)).sort(compare);

  return (
    <div className="portfolio-page">
      {error && <Alert variant="warning">{error} <Button variant="outline-dark" size="sm" onClick={fetchPortfolioData} disabled={refreshing}>Retry</Button></Alert>}
      <div className="page-header portfolio-page__header">
        <div>
          <span className="page-eyebrow">Investments</span>
          <h1>Portfolio</h1>
        </div>
        <div className="page-actions">
          <Button variant="outline-primary" disabled={refreshing} onClick={fetchPortfolioData}>{refreshing ? 'Refreshing…' : 'Refresh'}</Button>
          <Button variant="primary" onClick={() => setSearchParams({ tab: 'import' })}>Import holdings</Button>
          <Button variant="outline-primary" className="me-2" onClick={() => handleOpenAddTransaction('stock')}>
            Add Transaction
          </Button>
          <Button variant="outline-primary" onClick={handleOpenAddStock}>Add Stock</Button>
          <Button variant="outline-success" className="ms-2" onClick={handleOpenAddMutualFund}>
            Add Mutual Fund
          </Button>
        </div>
      </div>

      {/* Warning: stocks excluded from totals due to missing live price */}
      {unpricedStocks.length > 0 && (
        <Alert variant="warning" className="d-flex align-items-start gap-2" dismissible>
          <span className="portfolio-warning-icon"><AppIcon name="warning" /></span>
          <div>
            <strong>{unpricedStocks.length} stock{unpricedStocks.length > 1 ? 's' : ''} excluded from totals</strong> — no current market price available.
            {' '}These holdings ({unpricedStocks.map(s => s.isin.symbol).join(', ')}) are highlighted in the table below.
            <br />
            <small className="text-muted">
              Possible reasons: delisted / suspended stock, symbol mismatch, or exchange rate-limit. The last-known transaction price is shown in the table.
            </small>
          </div>
        </Alert>
      )}

      {/* Portfolio Summary */}
      <section className="portfolio-summary-grid" aria-label="Portfolio summary">
        <Card className="portfolio-summary-card portfolio-summary-card--value">
          <Card.Body>
            <Card.Title as="span">Total Value</Card.Title>
            <strong className="portfolio-summary-card__value text-primary">{formatCurrency(totalPortfolioValue)}</strong>
            {unpricedStocks.length > 0 && (
              <small className="portfolio-summary-card__meta">{unpricedStocks.length} stock{unpricedStocks.length > 1 ? 's' : ''} excluded</small>
            )}
          </Card.Body>
        </Card>
        <Card className="portfolio-summary-card">
          <Card.Body>
            <Card.Title as="span">Total Investment</Card.Title>
            <strong className="portfolio-summary-card__value">{formatCurrency(totalPortfolioCost)}</strong>
          </Card.Body>
        </Card>
        <Card className={`portfolio-summary-card ${totalPortfolioGain >= 0 ? 'portfolio-summary-card--positive' : 'portfolio-summary-card--negative'}`}>
          <Card.Body>
            <Card.Title as="span">Total Gain/Loss</Card.Title>
            <strong className={`portfolio-summary-card__value ${totalPortfolioGain >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(totalPortfolioGain)}
            </strong>
            <small className={`portfolio-summary-card__meta ${totalPortfolioGain >= 0 ? 'text-success' : 'text-danger'}`}>
              {totalPortfolioCost > 0 ? formatPercent((totalPortfolioGain / totalPortfolioCost) * 100) : '+0.00%'}
            </small>
          </Card.Body>
        </Card>
        <Card className={`portfolio-summary-card ${hasNumber(totalPortfolioDailyGain) && totalPortfolioDailyGain < 0 ? 'portfolio-summary-card--negative' : 'portfolio-summary-card--positive'}`}>
          <Card.Body>
            <Card.Title as="span">Today&apos;s Gain/Loss</Card.Title>
            <strong className={`portfolio-summary-card__value ${dailyGainClass(totalPortfolioDailyGain)}`}>
              {hasNumber(totalPortfolioDailyGain) ? formatCurrency(totalPortfolioDailyGain) : '—'}
            </strong>
            <small className={`portfolio-summary-card__meta ${dailyGainClass(totalPortfolioDailyGain)}`}>
              {hasNumber(totalPortfolioDailyGainPercent) ? formatPercent(totalPortfolioDailyGainPercent) : 'Awaiting market data'}
            </small>
          </Card.Body>
        </Card>
      </section>

      {['mutual-funds', 'stocks'].includes(activeTab) && <div className="holdings-toolbar">
        <Form.Group controlId="holding-search"><Form.Label>Find a holding</Form.Label><Form.Control type="search" placeholder="Search name, symbol or folio" value={holdingSearch} onChange={event => setHoldingSearch(event.target.value)} /></Form.Group>
        <Form.Group controlId="holding-sort"><Form.Label>Sort by</Form.Label><Form.Select value={holdingSort} onChange={event => setHoldingSort(event.target.value)}><option value="name">Name</option><option value="value">Highest value</option><option value="gain">Highest gain</option><option value="loss">Largest loss</option></Form.Select></Form.Group>
        {activeTab === 'stocks' && <Form.Group controlId="holding-account"><Form.Label>Broker account</Form.Label><Form.Select value={holdingAccount} onChange={event => setHoldingAccount(event.target.value)}><option value="all">All accounts</option>{brokerAccounts.map(account => <option key={account.id} value={account.id}>{account.display_name}</option>)}</Form.Select></Form.Group>}
        <p className="workspace-status">{activeTab === 'stocks' ? filteredStocks.length : filteredFunds.length} holdings shown · Summary includes all holdings</p>
      </div>}
      <Tabs activeKey={activeTab} onSelect={key => setSearchParams({ tab: key || 'mutual-funds' })} className="portfolio-tabs">
        <Tab eventKey="mutual-funds" title={`Mutual Funds (${mutualFunds.length})`}>
          <Card className="portfolio-holdings-panel">
            <Card.Header className="portfolio-panel-header">
              <div className="portfolio-panel-header__title">
                <span className="page-eyebrow">Holdings</span>
                <h2>Mutual Fund Holdings</h2>
              </div>
              <div className="portfolio-panel-header__tools">
                <dl className="portfolio-panel-metrics">
                  <div>
                    <dt>Value</dt>
                    <dd>{formatCurrency(totalMFValue)}</dd>
                  </div>
                  <div>
                    <dt>Gain</dt>
                    <dd className={totalMFGain >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(totalMFGain)}
                    </dd>
                  </div>
                  <div>
                    <dt>Today</dt>
                    <dd className={dailyGainClass(totalMFDailyGain)}>
                      {hasNumber(totalMFDailyGain) ? formatCurrency(totalMFDailyGain) : '—'}
                      {hasNumber(totalMFDailyGainPercent) && ` (${formatPercent(totalMFDailyGainPercent)})`}
                    </dd>
                  </div>
                </dl>
                <Button variant="outline-primary" size="sm" onClick={handleViewAllMFTransactions}>
                  All Transactions
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredFunds.length > 0 ? <Table responsive hover className="holdings-table mb-0" aria-label="Mutual fund holdings">
                <thead><tr><th>Holding</th><th>Current value</th><th>Today</th><th>Gain / loss</th><th title="Annualized return based on dated transactions">XIRR</th><th>Details</th></tr></thead>
                <tbody>{filteredFunds.map(fund => <tr key={fund.id}>
                  <td data-label="Holding"><Link to={'/mutual-funds/' + fund.isin.id}>{fund.isin.title}</Link><small>{fund.folio ? 'Folio: ' + fund.folio : 'AMFI: ' + fund.isin.stockid}</small></td>
                  <td data-label="Current value">{formatCurrency(fund.current_value)}</td>
                  <td data-label="Today">{renderDailyGain(fund.day_gain, fund.day_gain_percent)}</td>
                  <td data-label="Gain / loss" className={fund.total_gain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(fund.total_gain)}<small>{fund.total_cost > 0 ? formatPercent(fund.total_gain / fund.total_cost * 100) : 'Cost unavailable'}</small></td>
                  <td data-label="XIRR" className={hasNumber(fund.xirr) ? fund.xirr >= 0 ? 'text-success' : 'text-danger' : 'text-muted'} title={hasNumber(fund.xirr) ? 'Annualized return based on dated transactions' : 'Dated transaction history is insufficient'}>{hasNumber(fund.xirr) ? formatPercent(fund.xirr) : 'Unavailable'}</td>
                  <td data-label="Details"><Button variant="outline-primary" size="sm" onClick={() => setDetailHolding({ item: fund, type: 'mf' })} aria-label={'Details for ' + fund.isin.title}>View details</Button></td>
                </tr>)}</tbody>
              </Table> : <div className="workspace-empty"><p>{mutualFunds.length ? 'No holdings match your search.' : 'Add your first mutual fund or import a statement.'}</p><Button variant="outline-primary" onClick={handleOpenAddMutualFund}>Add Mutual Fund</Button></div>}
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="stocks" title={`Stocks (${stocks.length})`}>
          <Card className="portfolio-holdings-panel">
            <Card.Header className="portfolio-panel-header">
              <div className="portfolio-panel-header__title">
                <span className="page-eyebrow">Holdings</span>
                <h2>Stock Holdings</h2>
              </div>
              <div className="portfolio-panel-header__tools">
                <dl className="portfolio-panel-metrics">
                  <div>
                    <dt>Value</dt>
                    <dd>{formatCurrency(totalStockValue)}</dd>
                  </div>
                  <div>
                    <dt>Gain</dt>
                    <dd className={totalStockGain >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(totalStockGain)}
                    </dd>
                  </div>
                  <div>
                    <dt>Today</dt>
                    <dd className={dailyGainClass(totalStockDailyGain)}>
                      {hasNumber(totalStockDailyGain) ? formatCurrency(totalStockDailyGain) : '—'}
                      {hasNumber(totalStockDailyGainPercent) && ` (${formatPercent(totalStockDailyGainPercent)})`}
                    </dd>
                  </div>
                </dl>
                <Button variant="outline-primary" size="sm" onClick={handleViewAllStockTransactions}>
                  All Transactions
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredStocks.length > 0 ? <Table responsive hover className="holdings-table mb-0" aria-label="Stock holdings">
                <thead><tr><th>Holding</th><th>Current value</th><th>Today</th><th>Gain / loss</th><th title="Annualized return based on dated transactions">XIRR</th><th>Details</th></tr></thead>
                <tbody>{filteredStocks.map(stock => <tr key={stock.id}>
                  <td data-label="Holding"><Link to={'/stocks/' + stock.isin.id}>{stock.isin.symbol}</Link><small>{stock.isin.name}</small><small>{stock.account_count || stock.accounts?.length || 1} account(s){holdingAccount !== 'all' ? ' · combined holding value' : ''}</small>{!hasNumber(stock.current_price) && <Badge bg="warning" text="dark">Price unavailable</Badge>}{(stock.has_adjustments || (stock.adjustment_count ?? 0) > 0) && <Badge bg="info" text="dark">Adjusted</Badge>}</td>
                  <td data-label="Current value">{hasNumber(stock.current_value) ? formatCurrency(stock.current_value) : 'Unavailable · excluded'}</td>
                  <td data-label="Today">{renderDailyGain(stock.day_gain, stock.day_gain_percent)}</td>
                  <td data-label="Gain / loss" className={hasNumber(stock.total_gain) ? stock.total_gain >= 0 ? 'text-success' : 'text-danger' : ''}>{hasNumber(stock.total_gain) ? formatCurrency(stock.total_gain) : 'Unavailable'}</td>
                  <td data-label="XIRR" className={hasNumber(stock.xirr) ? stock.xirr >= 0 ? 'text-success' : 'text-danger' : 'text-muted'} title={hasNumber(stock.xirr) ? 'Annualized return based on dated transactions' : 'Dated transaction history is insufficient'}>{hasNumber(stock.xirr) ? formatPercent(stock.xirr) : 'Unavailable'}</td>
                  <td data-label="Details"><Button variant="outline-primary" size="sm" onClick={() => setDetailHolding({ item: stock, type: 'stock' })} aria-label={'Details for ' + stock.isin.symbol}>View details</Button></td>
                </tr>)}</tbody>
              </Table> : <div className="workspace-empty"><p>{stocks.length ? 'No holdings match these filters.' : 'Add your first stock or import a broker statement.'}</p><Button variant="outline-primary" onClick={handleOpenAddStock}>Add Stock</Button></div>}
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="corporate-actions" title="Corporate Actions">
          <CorporateActionHistory />
        </Tab>
        <Tab eventKey="import" title={`Import`}>
          <div className="d-flex flex-column gap-3">
            <Card className="portfolio-account-card">
              <Card.Body>
                <BrokerAccountsPanel accounts={brokerAccounts} onChange={refreshBrokerAccounts} onHoldingsMoved={fetchPortfolioData} />
              </Card.Body>
            </Card>
            <Card>
              <Card.Header>
                <h2 className="h5 mb-0">Import holdings</h2>
              </Card.Header>
              <Card.Body>
                <ol className="workspace-steps" aria-label="Import progress"><li aria-current={importStep === 1 ? 'step' : undefined}>1. Provider and account</li><li aria-current={importStep === 2 ? 'step' : undefined}>2. Choose statement</li><li aria-current={importStep === 3 ? 'step' : undefined}>3. Review and import</li></ol>
                <Form onSubmit={event => event.preventDefault()}>
                  <fieldset hidden={importStep !== 1} disabled={previewLoading || uploadLoading}>
                  <Form.Group className="mb-3" controlId="statement-field-1">
                    <Form.Label>Import Type</Form.Label>
                    <Form.Control as="select" value={uploadType} onChange={(e) => handleUploadTypeChange(e.target.value)}>
                      <option value="mutual_transactions">Mutual Fund - Transactions (CSV)</option>
                      <option value="zerodha_tradebook">Zerodha Tradebook (CSV/XLSX)</option>
                      <option value="zerodha_holdings">Zerodha Holdings Reconciliation (CSV/XLSX)</option>
                      <option value="groww_tradebook">Groww Tradebook (CSV/XLSX)</option>
                      <option value="upstox_tradebook">Upstox Tradebook (CSV/XLSX)</option>
                      <option value="angel_tradebook">Angel One Tradebook (CSV/XLSX)</option>
                      <option value="icici_direct_tradebook">ICICI Direct Tradebook (CSV/XLSX)</option>
                      <option value="other_broker_tradebook">Other Broker Tradebook (CSV/XLSX)</option>
                      <option value="cas_cams">CAS - CAMS only (PDF)</option>
                      <option value="cas_combined">CAS - CAMS + KFintech (PDF)</option>
                      <option value="cdsl_cas">CAS - CDSL eCAS (PDF)</option>
                      <option value="nsdl_cas">CAS - NSDL eCAS (PDF)</option>
                    </Form.Control>
                  </Form.Group>

                  {ACCOUNT_SCOPED_IMPORT_TYPES.has(uploadType) && (
                    <Form.Group className="mb-3" controlId="statement-field-2">
                      <Form.Label>Statement account</Form.Label>
                      <Form.Select
                        value={selectedBrokerAccountId}
                        onChange={(event) => setSelectedBrokerAccountId(event.target.value)}
                      >
                        <option value="">-- Select the account on this statement --</option>
                        {compatibleImportAccounts.map((account) => (
                          <option value={account.id} key={account.id}>
                            {account.display_name} ({account.provider_label})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {compatibleImportAccounts.length === 0
                          ? 'One-time setup: add this broker above, then select the saved account here. Move any legacy holdings first. See the Import guide for the steps.'
                          : 'Reuse the account on your statement. Then continue to choose your CSV, XLSX or PDF and review before importing.'}
                      </Form.Text>
                    </Form.Group>
                  )}

                  </fieldset>
                  <fieldset hidden={importStep !== 2} disabled={previewLoading || uploadLoading}>
                  {(uploadType === 'cas' || uploadType === 'cas_cams' || uploadType === 'cas_combined' || uploadType === 'cdsl_cas' || uploadType === 'nsdl_cas') && (
                    <Form.Group className="mb-3" controlId="statement-field-3">
                      <Form.Label>PDF Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Your PAN in uppercase, e.g. ABCDE1234F"
                        value={pdfPassword}
                        onChange={(e) => setPdfPassword(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        CAMS/KFintech statements usually use PAN as the password. CDSL/NSDL eCAS statements use the encrypted password sent with the statement email.
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3" controlId="statement-field-4">
                    <Form.Label>Choose file</Form.Label>
                    <Form.Control
                      type="file"
                      accept={(IMPORT_ALLOWED_EXT[uploadType] || []).join(',')}
                      onChange={handleFileChange}
                    />
                    {selectedFile && <small className="text-muted">Selected: {selectedFile.name}</small>}
                    {IMPORT_HINT[uploadType] && (
                      <Form.Text className="text-muted d-block mt-1">
                        {IMPORT_HINT[uploadType]}
                      </Form.Text>
                    )}
                    {uploadType === 'zerodha_holdings' && (
                      <Alert variant="info" className="mt-2 mb-0">
                        This reconciles quantities and cost basis using adjustment ledger entries. It does not create real buy/sell trades.
                      </Alert>
                    )}
                  </Form.Group>

                  </fieldset>
                  {uploadError && <Alert variant="danger">{uploadError}</Alert>}
                  {uploadSuccess && <Alert variant="success">{uploadSuccess}</Alert>}

                  <div className="page-actions">
                    {importStep === 1 ? <Button onClick={() => { setUploadError(''); setImportStep(2); }} disabled={ACCOUNT_SCOPED_IMPORT_TYPES.has(uploadType) && !selectedBrokerAccountId}>Continue</Button> : <>
                      <Button variant="outline-secondary" onClick={() => { setImportStep(1); setImportPreview(null); }} disabled={previewLoading || uploadLoading}>Back</Button>
                      <Button onClick={handlePreviewImport} disabled={!selectedFile || previewLoading || uploadLoading}>{previewLoading ? 'Checking file…' : 'Review statement'}</Button>
                    </>}
                  </div>
                  <Form.Text className="text-muted d-block mt-2">Supported: broker CSV/XLSX and CAS PDFs, up to 20 MB. Records are added after you confirm the review.</Form.Text>
                </Form>
              </Card.Body>
            </Card>

          </div>
        </Tab>
        <Tab eventKey="settings" title="Data settings">
            {/* Manage Data Card */}
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0 text-danger">Manage Data (Delete Transactions / Adjustments)</h5>
              </Card.Header>
              <Card.Body>
                {deleteSuccess && <Alert variant="success">{deleteSuccess}</Alert>}

                <Row>
                  <Col md={6}>
                    <h6>Bulk Actions</h6>
                    <p className="text-muted small">Clear ALL transactions for a category. Stock actions also clear stock adjustments. Use with caution.</p>
                    <div className="d-flex gap-2 mb-3">
                      <Button variant="outline-danger" onClick={() => handleOpenDeleteBulk('mf')}>
                        Clear All MF Transactions
                      </Button>
                      <Button variant="outline-danger" onClick={() => handleOpenDeleteBulk('stock')}>
                        Clear All Stock Transactions
                      </Button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6>Specific Holding</h6>
                    <p className="text-muted small">Clear transactions for a single holding. Stock actions also clear adjustments.</p>

                    <div className="d-flex gap-2 align-items-end">
                      <Form.Group style={{ width: '100%' }}>
                        <Form.Label>Select Holding</Form.Label>
                        <Form.Select
                          onChange={(e) => {
                            if (e.target.value) {
                              const [type, id] = e.target.value.split('-');
                              handleOpenDeleteSpecific(type as 'mf' | 'stock', Number(id));
                            }
                          }}
                          value=""
                        >
                          <option value="">-- Select to Delete --</option>
                          <optgroup label="Mutual Funds">
                            {mutualFunds.map(mf => (
                              <option key={`mf-${mf.id}`} value={`mf-${mf.id}`}>
                                {mf.isin.title}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Stocks">
                            {stockAccountOptions.map((option) => (
                              <option key={`stock-${option.holdingId}`} value={`stock-${option.holdingId}`}>
                                {option.label}
                              </option>
                            ))}
                          </optgroup>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

        </Tab>
      </Tabs>
      <Modal show={Boolean(importPreview)} onHide={() => { if (!uploadLoading) { setImportPreview(null); setImportStep(2); } }} size="lg" backdrop="static" aria-labelledby="import-review-title">
        <Modal.Header closeButton={!uploadLoading}><Modal.Title id="import-review-title">Review your statement</Modal.Title></Modal.Header>
        <Modal.Body>
          <dl className="import-review-details"><div><dt>File</dt><dd>{selectedFile?.name}</dd></div><div><dt>Destination</dt><dd>{brokerAccounts.find(account => String(account.id) === selectedBrokerAccountId)?.display_name || 'Your mutual fund portfolio'}</dd></div></dl>
          <p>{importPreview?.message}</p>
          {importPreview && importPreview.columns.length > 0 && <Table responsive aria-label="Statement source sample"><thead><tr>{importPreview.columns.map((column, i) => <th key={i}>{column || 'Column ' + (i + 1)}</th>)}</tr></thead><tbody>{importPreview.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody></Table>}
          {uploadError && <Alert variant="danger">{uploadError}</Alert>}
          {uploadLoading && <p role="status">Importing your statement. This may take a few minutes.</p>}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" disabled={uploadLoading} onClick={() => { setImportPreview(null); setImportStep(2); }}>Back to file</Button><Button disabled={uploadLoading} onClick={handleUpload}>{uploadLoading ? 'Importing…' : 'Confirm import'}</Button></Modal.Footer>
      </Modal>

      <Modal show={Boolean(detailHolding)} onHide={() => setDetailHolding(null)} size="lg" aria-labelledby="holding-details-title">
        <Modal.Header closeButton><Modal.Title id="holding-details-title">Holding details</Modal.Title></Modal.Header>
        <Modal.Body>{detailHolding && (() => {
          const item = detailHolding.item;
          const fund = detailHolding.type === 'mf' ? item as MutualFundHolding : null;
          const stock = detailHolding.type === 'stock' ? item as StockHolding : null;
          const accountRows = stock?.accounts || [];
          const primary = { ...item, id: accountRows[0]?.holding_id || item.id, notes: accountRows[0]?.notes || item.notes };
          return <>
            <h2 className="h5">{fund?.isin.title || stock?.isin.name}</h2>
            <dl className="holding-detail-metrics">
              <div><dt>{fund ? 'Units' : 'Shares'}</dt><dd>{item.no_shares.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</dd></div>
              <div><dt>{fund ? 'NAV' : 'Price'}</dt><dd>{hasNumber(fund?.current_nav ?? stock?.current_price) ? formatCurrency((fund?.current_nav ?? stock?.current_price)!) : 'Unavailable'}</dd></div>
              <div><dt>Current value</dt><dd>{hasNumber(item.current_value) ? formatCurrency(item.current_value) : 'Unavailable'}</dd></div>
              <div><dt>Cost basis</dt><dd>{fund ? formatCurrency(fund.total_cost) : hasNumber(item.current_value) && hasNumber(item.total_gain) ? formatCurrency(item.current_value - item.total_gain) : 'Unavailable'}</dd></div>
              <div><dt>Annualized return (XIRR)</dt><dd>{hasNumber(item.xirr) ? formatPercent(item.xirr) : 'Unavailable: dated history is insufficient'}</dd></div>
              {fund && <div><dt>NAV date</dt><dd>{fund.nav_date || 'Unavailable'}</dd></div>}
            </dl>
            <p className="workspace-status">XIRR accounts for the amounts and dates of your transactions. It is different from the total gain percentage.</p>
            {stock && accountRows.length > 0 && <div className="portfolio-account-breakdown__grid">{accountRows.map(row => {
              const accountHolding = { ...stock, id: row.holding_id, no_shares: row.no_shares, notes: row.notes || '' };
              return <article className="portfolio-account-position" key={row.holding_id}>
                <strong>{row.broker_account.display_name}</strong><p className="workspace-status">{row.broker_account.provider_label}</p>
                <dl><div><dt>Shares</dt><dd>{row.no_shares}</dd></div><div><dt>Cost</dt><dd>{formatCurrency(row.total_cost)}</dd></div><div><dt>Value</dt><dd>{hasNumber(row.current_value) ? formatCurrency(row.current_value) : 'Unavailable'}</dd></div><div><dt>Gain</dt><dd>{hasNumber(row.total_gain) ? formatCurrency(row.total_gain) : 'Unavailable'}</dd></div></dl>
                <small>{row.latest_statement_date ? 'Latest statement: ' + row.latest_statement_date : 'No final holdings statement imported yet'}</small>
                <div className="portfolio-account-position__actions">
                  <Button size="sm" onClick={() => { setDetailHolding(null); handleOpenAddTransaction('stock', row.holding_id); }}>Add transaction</Button>
                  <Button size="sm" variant="outline-primary" onClick={() => { setDetailHolding(null); handleEdit(accountHolding, 'stock'); }}>Edit notes</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => { setDetailHolding(null); handleViewTransactions(accountHolding, 'stock'); }}>View transactions</Button>
                </div>
              </article>;
            })}</div>}
            {(!stock || !accountRows.length) && <div className="page-actions">
              <Button onClick={() => { setDetailHolding(null); handleOpenAddTransaction(detailHolding.type, primary.id); }}>Add transaction</Button>
              <Button variant="outline-primary" onClick={() => { setDetailHolding(null); handleEdit(primary, detailHolding.type); }}>Edit notes</Button>
              <Button variant="outline-secondary" onClick={() => { setDetailHolding(null); handleViewTransactions(primary, detailHolding.type); }}>View transactions</Button>
            </div>}
          </>;
        })()}</Modal.Body>
      </Modal>


      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to clear transactions?</p>
          <div className="alert alert-warning">
            <strong>Action:</strong> {deleteType === 'bulk' ? `Clear ALL ${deleteCategory === 'mf' ? 'Mutual Fund' : 'Stock'} Transactions` : `Clear transactions for selected holding`}
            <br />
            <strong>Consequence:</strong> Your holding units/shares and costs will be reset to 0. Notes will be preserved.
            {deleteCategory === 'stock' && (
              <>
                <br />
                Stock reconciliation adjustments for the selected scope will also be cleared.
              </>
            )}
          </div>
          {deleteError && <Alert variant="danger">{deleteError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? <Spinner animation="border" size="sm" /> : 'Confirm Clear'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit {editType === 'mf' ? 'Mutual Fund' : 'Stock'} Holding</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && <Alert variant="danger">{editError}</Alert>}
          {editingItem && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editType === 'mf' ? editingItem.isin?.title : editingItem.isin?.symbol}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Shares/Units</Form.Label>
                <Form.Control
                  type="number"
                  value={editingItem.no_shares}
                  disabled
                />
                <Form.Text className="text-muted">
                  To change shares, add a transaction
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editingItem.notes || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                  placeholder="Add notes about this holding..."
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Transactions Modal */}
      <Modal show={showTransactionsModal} onHide={() => setShowTransactionsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Transactions - {selectedItem && (editType === 'mf' ? selectedItem.isin?.title : selectedItem.isin?.symbol)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingTransactions ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : transactions.length > 0 ? (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Amount</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr key={`${txn.entry_kind || 'trade'}-${txn.id}`}>
                    <td>{new Date(txn.date).toLocaleDateString('en-IN')}</td>
                    <td>
                      <Badge bg={
                        txn.entry_kind === 'adjustment'
                          ? 'info'
                          : (txn.transaction_type.toLowerCase() === 'buy' || txn.transaction_type.toLowerCase() === 'purchase' ? 'success' : 'danger')
                      }>
                        {txn.transaction_type}
                      </Badge>
                      {txn.entry_kind === 'adjustment' && (
                        <div className="text-muted small mt-1">Adjustment</div>
                      )}
                    </td>
                    <td>{txn.quantity}</td>
                    <td>{txn.entry_kind === 'adjustment' ? '—' : `₹${txn.price.toFixed(2)}`}</td>
                    <td>{txn.entry_kind === 'adjustment' ? `${formatCurrency(txn.amount)} cost delta` : formatCurrency(txn.amount)}</td>
                    <td>
                      <small className="text-muted">
                        {txn.notes || '-'}
                        {txn.entry_kind === 'adjustment' && txn.source && (
                          <>
                            <br />
                            Source: {txn.source}
                            {txn.reference ? ` (${txn.reference})` : ''}
                          </>
                        )}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center p-4 text-muted">
              No transactions found
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransactionsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Portfolio-wide Mutual Fund Transaction Ledger */}
      <Modal
        show={showAllMFTransactionsModal}
        onHide={() => setShowAllMFTransactionsModal(false)}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>All Mutual Fund Transactions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {allMFTransactionsError && <Alert variant="danger">{allMFTransactionsError}</Alert>}

          <Row className="g-2 mb-3">
            <Col lg={4}>
              <Form.Control
                type="search"
                placeholder="Search fund, folio, ISIN, type, or notes"
                value={mfTransactionSearch}
                onChange={(event) => {
                  setMFTransactionSearch(event.target.value);
                  setMFTransactionPage(1);
                }}
              />
            </Col>
            <Col sm={4} lg={2}>
              <Form.Select
                value={mfTransactionType}
                onChange={(event) => {
                  setMFTransactionType(event.target.value);
                  setMFTransactionPage(1);
                }}
              >
                <option value="">All transaction types</option>
                {mfTransactionTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={4} lg={2}>
              <Form.Control
                type="date"
                aria-label="Transactions from date"
                value={mfTransactionDateFrom}
                onChange={(event) => {
                  setMFTransactionDateFrom(event.target.value);
                  setMFTransactionPage(1);
                }}
              />
            </Col>
            <Col sm={4} lg={2}>
              <Form.Control
                type="date"
                aria-label="Transactions to date"
                value={mfTransactionDateTo}
                onChange={(event) => {
                  setMFTransactionDateTo(event.target.value);
                  setMFTransactionPage(1);
                }}
              />
            </Col>
            <Col lg={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setMFTransactionSearch('');
                  setMFTransactionType('');
                  setMFTransactionDateFrom('');
                  setMFTransactionDateTo('');
                  setMFTransactionPage(1);
                }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>

          {!loadingAllMFTransactions && (
            <Row className="g-2 mb-3">
              <Col md={4}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Matching transactions</div>
                  <strong>{filteredMFTransactions.length.toLocaleString('en-IN')}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Purchases / inflows</div>
                  <strong className="text-success">{formatCurrency(filteredPurchaseTotal)}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Redemptions / outflows</div>
                  <strong className="text-danger">{formatCurrency(filteredRedemptionTotal)}</strong>
                </div>
              </Col>
            </Row>
          )}

          {loadingAllMFTransactions ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : pagedMFTransactions.length > 0 ? (
            <>
              <Table striped hover responsive className="align-middle mb-2">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Fund / Folio</th>
                    <th>Type</th>
                    <th className="text-end">Units</th>
                    <th className="text-end">NAV</th>
                    <th className="text-end">Amount</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedMFTransactions.map((txn) => {
                    const isRedemption = /redempt|sell|switch.?out/i.test(txn.transaction_type);
                    return (
                      <tr key={txn.id}>
                        <td className="text-nowrap">
                          {new Date(`${txn.date}T00:00:00`).toLocaleDateString('en-IN')}
                        </td>
                        <td style={{ minWidth: '260px' }}>
                          <div className="fw-semibold">{txn.fund_name}</div>
                          <small className="text-muted">
                            {txn.folio ? `Folio ${txn.folio}` : 'Folio unavailable'}
                            {txn.isin ? ` · ${txn.isin}` : ''}
                          </small>
                        </td>
                        <td>
                          <Badge bg={isRedemption ? 'danger' : 'success'}>
                            {txn.transaction_type}
                          </Badge>
                        </td>
                        <td className="text-end">{txn.quantity.toFixed(4)}</td>
                        <td className="text-end">
                          {txn.price ? `₹${txn.price.toFixed(4)}` : '—'}
                        </td>
                        <td className={`text-end fw-semibold ${txn.amount < 0 ? 'text-danger' : ''}`}>
                          {formatCurrency(txn.amount)}
                        </td>
                        <td style={{ minWidth: '180px' }}>
                          <small className="text-muted">{txn.notes || '—'}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <small className="text-muted">
                  Showing {((safeMFTransactionPage - 1) * mfTransactionPageSize) + 1}–
                  {Math.min(safeMFTransactionPage * mfTransactionPageSize, filteredMFTransactions.length)}
                  {' '}of {filteredMFTransactions.length.toLocaleString('en-IN')}
                </small>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={safeMFTransactionPage <= 1}
                    onClick={() => setMFTransactionPage(safeMFTransactionPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="small">
                    Page {safeMFTransactionPage} of {mfTransactionPageCount}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={safeMFTransactionPage >= mfTransactionPageCount}
                    onClick={() => setMFTransactionPage(safeMFTransactionPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center p-5 text-muted">
              No mutual fund transactions match these filters.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAllMFTransactionsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Portfolio-wide Stock Transaction Ledger */}
      <Modal
        show={showAllStockTransactionsModal}
        onHide={() => setShowAllStockTransactionsModal(false)}
        size="xl"
        aria-labelledby="all-stock-transactions-title"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title id="all-stock-transactions-title">All Stock Transactions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {allStockTransactionsError && <Alert variant="danger">{allStockTransactionsError}</Alert>}

          <Row className="g-2 mb-3">
            <Col lg={4}>
              <Form.Control
                type="search"
                aria-label="Search stock transactions"
                placeholder="Search stock, symbol, ISIN, type, or notes"
                value={stockTransactionSearch}
                onChange={(event) => {
                  setStockTransactionSearch(event.target.value);
                  setStockTransactionPage(1);
                }}
              />
            </Col>
            <Col sm={4} lg={2}>
              <Form.Select
                aria-label="Stock transaction type"
                value={stockTransactionType}
                onChange={(event) => {
                  setStockTransactionType(event.target.value);
                  setStockTransactionPage(1);
                }}
              >
                <option value="">All transaction types</option>
                {stockTransactionTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={4} lg={2}>
              <Form.Control
                type="date"
                aria-label="Stock transactions from date"
                value={stockTransactionDateFrom}
                onChange={(event) => {
                  setStockTransactionDateFrom(event.target.value);
                  setStockTransactionPage(1);
                }}
              />
            </Col>
            <Col sm={4} lg={2}>
              <Form.Control
                type="date"
                aria-label="Stock transactions to date"
                value={stockTransactionDateTo}
                onChange={(event) => {
                  setStockTransactionDateTo(event.target.value);
                  setStockTransactionPage(1);
                }}
              />
            </Col>
            <Col lg={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setStockTransactionSearch('');
                  setStockTransactionType('');
                  setStockTransactionDateFrom('');
                  setStockTransactionDateTo('');
                  setStockTransactionPage(1);
                }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>

          {!loadingAllStockTransactions && (
            <Row className="g-2 mb-3">
              <Col sm={6} xl={3}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Matching entries</div>
                  <strong>{filteredStockTransactions.length.toLocaleString('en-IN')}</strong>
                </div>
              </Col>
              <Col sm={6} xl={3}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Buys</div>
                  <strong className="text-success">{formatCurrency(filteredStockBuyTotal)}</strong>
                </div>
              </Col>
              <Col sm={6} xl={3}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Sells</div>
                  <strong className="text-danger">{formatCurrency(filteredStockSellTotal)}</strong>
                </div>
              </Col>
              <Col sm={6} xl={3}>
                <div className="border rounded p-2 h-100">
                  <div className="small text-muted">Adjustments</div>
                  <strong>{filteredStockAdjustmentCount.toLocaleString('en-IN')}</strong>
                </div>
              </Col>
            </Row>
          )}

          {loadingAllStockTransactions ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : pagedStockTransactions.length > 0 ? (
            <>
              <Table striped hover responsive className="align-middle mb-2">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Stock</th>
                    <th>Entry</th>
                    <th>Type</th>
                    <th className="text-end">Quantity</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Amount</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedStockTransactions.map((txn) => {
                    const isAdjustment = txn.entry_kind === 'adjustment';
                    const isSell = /sell/i.test(txn.transaction_type);
                    return (
                      <tr key={`${txn.entry_kind}-${txn.id}`}>
                        <td className="text-nowrap">
                          {new Date(`${txn.date}T00:00:00`).toLocaleDateString('en-IN')}
                        </td>
                        <td style={{ minWidth: '210px' }}>
                          <div className="fw-semibold">{txn.symbol}</div>
                          <small className="text-muted">{txn.stock_name} · {txn.isin}</small>
                        </td>
                        <td>
                          <Badge bg={isAdjustment ? 'info' : 'secondary'} text={isAdjustment ? 'dark' : undefined}>
                            {isAdjustment ? 'Adjustment' : 'Trade'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={isAdjustment ? 'info' : (isSell ? 'danger' : 'success')} text={isAdjustment ? 'dark' : undefined}>
                            {txn.transaction_type}
                          </Badge>
                        </td>
                        <td className="text-end">{txn.quantity.toFixed(2)}</td>
                        <td className="text-end">{isAdjustment ? '—' : `₹${txn.price.toFixed(2)}`}</td>
                        <td className={`text-end fw-semibold ${!isAdjustment && isSell ? 'text-danger' : ''}`}>
                          {formatCurrency(txn.amount)}
                        </td>
                        <td style={{ minWidth: '190px' }}>
                          <small className="text-muted">
                            {txn.notes || '—'}
                            {(txn.source || txn.reference) && (
                              <>
                                <br />
                                {[txn.source, txn.reference].filter(Boolean).join(' · ')}
                              </>
                            )}
                          </small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <small className="text-muted">
                  Showing {((safeStockTransactionPage - 1) * stockTransactionPageSize) + 1}–
                  {Math.min(safeStockTransactionPage * stockTransactionPageSize, filteredStockTransactions.length)}
                  {' '}of {filteredStockTransactions.length.toLocaleString('en-IN')}
                </small>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={safeStockTransactionPage <= 1}
                    onClick={() => setStockTransactionPage(safeStockTransactionPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="small">
                    Page {safeStockTransactionPage} of {stockTransactionPageCount}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={safeStockTransactionPage >= stockTransactionPageCount}
                    onClick={() => setStockTransactionPage(safeStockTransactionPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center p-5 text-muted">
              No stock transactions match these filters.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAllStockTransactionsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Transaction Modal */}
      <Modal show={showAddTransactionModal} onHide={() => setShowAddTransactionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addTransactionError && (
            <Alert variant="danger" onClose={() => setAddTransactionError('')} dismissible>
              {addTransactionError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={addTransactionType}
                onChange={(e) => {
                  setAddTransactionType(e.target.value as 'mf' | 'stock');
                  setSelectedHoldingId(null);
                  setNewTransaction(prev => ({
                    ...prev,
                    txn_type: e.target.value === 'mf' ? 'purchase' : 'buy'
                  }));
                }}
              >
                <option value="stock">Stock</option>
                <option value="mf">Mutual Fund</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select {addTransactionType === 'mf' ? 'Mutual Fund' : 'Stock'}</Form.Label>
              <Form.Select
                value={selectedHoldingId || ''}
                onChange={(e) => setSelectedHoldingId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">-- Select --</option>
                {addTransactionType === 'mf'
                  ? mutualFunds.map((mf) => (
                    <option key={mf.id} value={mf.id}>
                      {mf.isin?.title || 'Unknown Fund'}
                    </option>
                  ))
                  : stockAccountOptions.map((option) => (
                    <option key={option.holdingId} value={option.holdingId}>
                      {option.label}
                    </option>
                  ))
                }
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Transaction Date</Form.Label>
              <Form.Control
                type="date"
                value={newTransaction.date}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, date: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Transaction Type</Form.Label>
              <Form.Select
                value={newTransaction.txn_type}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, txn_type: e.target.value }))}
              >
                {addTransactionType === 'mf' ? (
                  <>
                    <option value="purchase">Purchase</option>
                    <option value="sip">SIP</option>
                    <option value="redeem">Redeem</option>
                  </>
                ) : (
                  <>
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                  </>
                )}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{addTransactionType === 'mf' ? 'Units' : 'Quantity'}</Form.Label>
              <Form.Control
                type="number"
                step="0.001"
                placeholder="Enter quantity"
                value={newTransaction.quantity}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, quantity: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{addTransactionType === 'mf' ? 'NAV' : 'Price'} (₹)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                placeholder="Enter price per unit"
                value={newTransaction.price}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, price: e.target.value }))}
              />
            </Form.Group>

            {newTransaction.quantity && newTransaction.price && (
              <Alert variant="info">
                <strong>Total Amount:</strong> {formatCurrency(parseFloat(newTransaction.quantity) * parseFloat(newTransaction.price))}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Add any notes..."
                value={newTransaction.notes}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddTransactionModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddTransaction}
            disabled={addTransactionLoading || !selectedHoldingId || !newTransaction.quantity || !newTransaction.price}
          >
            {addTransactionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              'Add Transaction'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <AddStockToPortfolioModal
        show={showAddStockModal}
        searchable
        onHide={() => setShowAddStockModal(false)}
        onSuccess={() => fetchPortfolioData()}
      />

      <AddMutualFundToPortfolioModal
        show={showAddMutualFundModal}
        searchable
        onHide={() => setShowAddMutualFundModal(false)}
        onSuccess={() => fetchPortfolioData()}
      />
    </div>
  );
};

export default Portfolio;
